import React, { useEffect, useState } from 'react';
import c from '../../constant';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Button, Box, Flex, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import useStore from '../../store';
import DropDownField from '../form/DropDownField';
import CheckBoxFormField from '../form/CheckBoxFormField';

const filterSchema = Yup.object().shape({
	no: Yup.number(),
	rank: Yup.string(),
	position: Yup.string(),
	office: Yup.string(),
	department: Yup.string(),
	createdDate: Yup.string(),
	examCandidates: Yup.boolean(),
});

function OfficialListFilter({
	isOpen,
	onClose,
	callback,
	no = '',
	rank = '',
	position = '',
	office = '',
	department = '',
	dateStart = null,
	dateEnd = null,
	examCandidates = null,
}) {
	moment.locale('en-gb');
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);

	let defaultRange = '';
	if (dateStart && dateEnd) {
		const defaultFrom = JSON.parse(dateStart);

		const defaultTo = JSON.parse(dateEnd);
		defaultRange = {
			from: defaultFrom,
			to: defaultTo,
		};
	}

	const OFFICE_OPTIONS_BY_DEPARTMENT = {
		D_G_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative', value: 'administrative' },
			{ label: lang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting', value: 'accounting' },
			{ label: lang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource', value: 'human_resource' },
			{ label: lang === 'kh' ? 'ការិយាល័យទំនាក់ទំនងសាធារណៈ' : 'Public Relation', value: 'public_relation' },
		],

		D_L_L_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing', value: 'licensing' },
			{ label: lang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration', value: 'intermediary_registration' },
			{ label: lang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation', value: 'legal_and_mediation' },
		],

		D_S: [
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer', value: 'life_insurer' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer', value: 'general_insurer' },
			{
				label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'Micro Insurer',
				value: 'micro_insurer',
			},
			{ label: lang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential', value: 'prudential' },
		],

		D_M_D: [
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product', value: 'life_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product', value: 'general_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product', value: 'micro_insurance_product' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision', value: 'market_conduct_supervision' },
		],

		D_R_T_C: [
			{ label: lang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research', value: 'research' },
			{ label: lang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training', value: 'training' },
			{ label: lang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation', value: 'cooperation' },
			{ label: lang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech', value: 'insurtech' },
		],
	};

	return (
		<>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							initialValues={{
								department: department ? department : '',
								office: office ? office : '',
								createdDate: defaultRange ? JSON.stringify(defaultRange) : '',
								examCandidates: examCandidates === true || examCandidates === 'true',
							}}
							validationSchema={filterSchema}
							onSubmit={(values) => {
								let url = '?page=1';
								const officeList = OFFICE_OPTIONS_BY_DEPARTMENT[values?.department] || [];
								const isOfficeValidForDepartment = officeList?.some((item) => item.value === values.office);

								if (values?.department) {
									url += `&department=${values?.department}`;
								}

								if (values?.department && values?.office && isOfficeValidForDepartment) {
									url += `&office=${values?.office}`;
								}

								if (values?.createdDate) {
									const tmpDate = JSON.parse(values?.createdDate);
									if (tmpDate?.from && tmpDate?.to) {
										const dateStart = JSON.stringify(tmpDate.from);
										const dateEnd = JSON.stringify(tmpDate.to);
										url += `&dateStart=${dateStart}&dateEnd=${dateEnd}`;
									}
								}

								if (values?.examCandidates === true) {
									url += `&examCandidates=true`;
								}
								callback(url);
								onClose();
							}}
						>
							{(props) => (
								<Box pb="4">
									<Form>
										<DropDownField
											{...props}
											label="Department"
											name="department"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ label: 'General Affairs Department', value: 'D_G_A' },
												{ label: 'License and Legal Affairs Department', value: 'D_L_L_A' },
												{ label: 'Supervision Department', value: 'D_S' },
												{ label: 'Market Development Department', value: 'D_M_D' },
												{ label: 'Research, Training and Cooperation Department', value: 'D_R_T_C' },
											]}
										/>
										{props.values.department && (
											<DropDownField
												{...props}
												key={props.values.department}
												label="Office"
												name="office"
												placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
												options={OFFICE_OPTIONS_BY_DEPARTMENT[props.values.department] || []}
											/>
										)}

										<CheckBoxFormField {...props} label="Exam Candidates" name="examCandidates" />

										<Flex mt="32px" justifyContent="flex-end">
											<Button
												id="login-btn"
												variant="solid"
												size="md"
												h="42px"
												w="128px"
												type="submit"
												borderRadius={c.borderRadius}
												color="#3498db"
											>
												{t('Apply')}
											</Button>
											<Button
												size="md"
												h="42px"
												ml={4}
												w="128px"
												onClick={() => {
													callback('?page=1');
													onClose();
												}}
												variant="ghost"
											>
												{t('Clear')}
											</Button>
										</Flex>
									</Form>
								</Box>
							)}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}

export default OfficialListFilter;
